// Derives what each tooth looks like now from its entry history.
import type { Entry } from '@/types/api';
import { type Code, isPlanned, type Material, type Surface } from './dental';

export type SurfaceMark = {
  kind: 'caries' | 'filling' | 'inlay' | 'sealant' | 'fracture' | 'wear';
  material: Material | null;
  /** Work performed in the visit shown (green); otherwise existing state (blue). */
  performed: boolean;
};

/** remnant: only the root is left (crown lost); the tooth still holds its place. */
export type Presence = 'present' | 'missing' | 'implant' | 'pontic' | 'remnant';

export type ToothState = {
  tooth: number;
  presence: Presence;
  /** The extraction or implant was performed in the visit shown. */
  presencePerformed: boolean;
  surfaces: Partial<Record<Surface, SurfaceMark>>;
  /** Finding or restoration recorded without a surface. */
  whole: SurfaceMark | null;
  crown: { material: Material | null; performed: boolean } | null;
  veneer: boolean;
  abutment: boolean;
  rootCanal: boolean;
  rootCanalPerformed: boolean;
  periapical: boolean;
  /** Non-vital pulp. */
  devital: boolean;
  fracture: boolean;
  mobility: string | null;
  calculus: boolean;
  impacted: boolean;
  planned: Entry[];
  lastChange: string | null;
};

export function emptyTooth(tooth: number): ToothState {
  return {
    tooth,
    presence: 'present',
    presencePerformed: false,
    surfaces: {},
    whole: null,
    crown: null,
    veneer: false,
    abutment: false,
    rootCanal: false,
    rootCanalPerformed: false,
    periapical: false,
    devital: false,
    fracture: false,
    mobility: null,
    calculus: false,
    impacted: false,
    planned: [],
    lastChange: null,
  };
}

const SURFACE_KIND: Partial<Record<Code, SurfaceMark['kind']>> = {
  caries: 'caries',
  filling: 'filling',
  inlay: 'inlay',
  sealant: 'sealant',
  fracture: 'fracture',
  wear: 'wear',
};

const RESTORES_REMNANT: ReadonlySet<Code> = new Set([
  'crown',
  'bridge_abutment',
  'filling',
  'inlay',
  'veneer',
]);

function clearFindings(s: ToothState) {
  for (const key of Object.keys(s.surfaces) as Surface[]) {
    const kind = s.surfaces[key]?.kind;
    if (kind === 'caries' || kind === 'fracture') delete s.surfaces[key];
  }
  if (s.whole?.kind === 'caries' || s.whole?.kind === 'fracture') s.whole = null;
  s.fracture = false;
  s.periapical = false;
  s.mobility = null;
}

function removeTooth(s: ToothState, presence: Presence, performed: boolean) {
  const keep = { tooth: s.tooth, planned: s.planned };
  Object.assign(s, emptyTooth(s.tooth), keep, { presence, presencePerformed: performed });
}

/** "II. fok", "II. stepen", "Grade II", "Grad II" → "II"; any other note → "•". */
function mobilityGrade(note: string | null): string {
  const m = note?.match(/(I{1,3})\. (?:fok|stepen)|(?:Grade|Grad) (I{1,3})\b/);
  return m?.[1] ?? m?.[2] ?? '•';
}

/** `performed`: the entry is work performed in the visit being shown. */
export function applyEntry(s: ToothState, e: Entry, performed = false): void {
  if (isPlanned(e.note)) {
    s.planned.push(e);
    return;
  }
  s.lastChange = e.createdAt;
  // A root remnant built up again (post and core, crown, filling) is a tooth again.
  if (s.presence === 'remnant' && RESTORES_REMNANT.has(e.code)) s.presence = 'present';
  const kind = SURFACE_KIND[e.code];
  if (kind) {
    const mark: SurfaceMark = { kind, material: e.material, performed };
    if (e.surfaces.length) {
      for (const surface of e.surfaces) s.surfaces[surface] = mark;
    } else {
      s.whole = mark;
    }
    if (kind === 'fracture') s.fracture = true;
    return;
  }
  switch (e.code) {
    case 'crown':
      s.crown = { material: e.material, performed };
      break;
    case 'veneer':
      s.veneer = true;
      break;
    case 'root_canal':
      s.rootCanal = true;
      s.rootCanalPerformed = performed;
      break;
    case 'periapical':
      s.periapical = true;
      break;
    case 'devital':
      s.devital = true;
      break;
    case 'root_remnant': {
      // The crown is gone; what is known about the root stays.
      const root = {
        rootCanal: s.rootCanal,
        rootCanalPerformed: s.rootCanalPerformed,
        periapical: s.periapical,
        devital: s.devital,
      };
      removeTooth(s, 'remnant', false);
      Object.assign(s, root);
      break;
    }
    case 'extraction':
    case 'missing':
      removeTooth(s, 'missing', performed);
      break;
    case 'implant':
      removeTooth(s, 'implant', performed);
      if (e.material) s.crown = { material: e.material, performed };
      break;
    case 'bridge_pontic':
      removeTooth(s, 'pontic', performed);
      s.crown = { material: e.material, performed };
      break;
    case 'bridge_abutment':
      s.abutment = true;
      s.crown = { material: e.material, performed };
      break;
    case 'mobility':
      s.mobility = mobilityGrade(e.note);
      break;
    case 'calculus':
      s.calculus = true;
      break;
    case 'impacted':
      s.impacted = true;
      break;
    case 'sound':
      clearFindings(s);
      s.calculus = false;
      break;
    default:
      break;
  }
}

/**
 * The chart: every non-rejected entry applied in recording order. `isCurrent`
 * tells which entries belong to the visit shown, so that work performed in it
 * stands out (green) from the existing state (blue).
 */
export function deriveChart(
  entries: readonly Entry[],
  isCurrent: (e: Entry) => boolean = () => false
): Map<number, ToothState> {
  const chart = new Map<number, ToothState>();
  const ordered = [...entries]
    .filter((e) => e.status !== 'REJECTED')
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  for (const e of ordered) {
    let s = chart.get(e.tooth);
    if (!s) {
      s = emptyTooth(e.tooth);
      chart.set(e.tooth, s);
    }
    applyEntry(s, e, e.kind === 'PERFORMED' && isCurrent(e));
  }
  return chart;
}
